/**
 * Web Push: the service worker, the subscription, and the four `/push` routes.
 *
 * Every call this feature makes to the daemon is in this file, and that is the
 * point rather than tidiness. The server half -- `hm-notify`, per-tenant VAPID
 * keys, `push_registrations`, `pushapi.rs` -- does not exist yet, so this is
 * written against the contract in §11 of `docs/push-notifications.md` and has to
 * survive meeting a daemon that has never heard of it. One module means one
 * place where a 404 becomes {@link supported} = `"no"`, and one thing for the UI
 * to ask before it renders anything at all. A settings screen with a toggle that
 * cannot work is worse than a settings screen with nothing.
 *
 * # Why the permission prompt is not here
 *
 * `Notification.requestPermission()` is called from exactly one place, a button
 * in Settings, and never on load. A cold prompt is the fastest way to be
 * permanently blocked: `denied` cannot be undone by any code on this page, in
 * any browser, ever. It is not a state to recover from, it is a state to avoid
 * entering, and the only way to avoid it is to ask someone who has just said
 * they want this.
 *
 * # Why re-subscribing does not accumulate rows
 *
 * Three things, and each catches what the others miss. `getSubscription()` is
 * consulted before `subscribe()` is ever called, so a launch on a browser that
 * already has one does not mint a second. `POST /push/registrations` is an
 * UPSERT on `(transport, endpoint)` -- §11 -- so a re-registration refreshes a
 * row instead of adding one. And the browser hands back the same endpoint
 * anyway. The failure this avoids is silent: nothing looks wrong until a phone
 * buzzes four times for one message.
 *
 * # What is deliberately not here
 *
 * No fetch of message content from the push handler. Web Push encrypts payloads
 * end to end (RFC 8291), which is why §4 lets the daemon put a sender in one at
 * all -- and which half of the message a device may be told is a per-tenant
 * setting on the server. A client that filled a blank payload in from its own
 * JMAP session would be quietly overriding that setting from the one place the
 * person who set it cannot see.
 */

import { onToastAction, request as transportRequest } from "@transport";
import { authHeaders } from "./settings";
import { signal } from "@preact/signals";

import * as actions from "./actions";
import * as delta from "./delta";
import * as jmap from "./jmap";
import { messageFromLink } from "./navigation";
import { isPublicSession } from "./publicSession";
import * as store from "./store";

/** Whether this daemon serves `/push` at all. Unknown until something asks. */
export const supported = signal<"unknown" | "yes" | "no">("unknown");

/**
 * Whether this *browser* could do it, which is a different question.
 *
 * On iOS the answer is no for a Safari tab and yes for the same page once it
 * has been added to the Home Screen, so this is also the thing that decides
 * whether Settings explains the Share sheet or offers a button.
 */
export const browserCapable = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

/** Mirrors `Notification.permission`, as a signal so the UI re-renders on it. */
export const permission = signal<NotificationPermission>(
  browserCapable ? Notification.permission : "default",
);

/**
 * Notice when the permission is changed outside the page.
 *
 * The dead end this removes: someone is told notifications are blocked and that
 * only the browser's own settings can undo it, unblocks them in the panel
 * behind the address bar -- which does not reload the page or unmount anything
 * -- and comes back to a screen still saying they are blocked, with no way
 * forward but a reload nobody thought to suggest. `PermissionStatus` fires for
 * exactly this, and it is the only signal a page gets.
 */
if (browserCapable && navigator.permissions) {
  void navigator.permissions
    .query({ name: "notifications" as PermissionName })
    .then((status) => {
      status.addEventListener("change", () => (permission.value = Notification.permission));
    })
    .catch(() => {
      // Older Safari refuses this query rather than answering it. The card is
      // then merely stale until the next mount, which is where it started.
    });
}

/** This device's row on the server, once there is one. */
export const enabled = signal<Enrolment | null>(read());

/** Set when something the user asked for failed, cleared when they retry. */
export const problem = signal<string | null>(null);

/** True while a subscribe or unsubscribe is in flight. */
export const busy = signal(false);

export interface Enrolment {
  /** The `push_registrations` row id, from the 201. */
  id: number;
  /** Kept so a stale enrolment can be told from the live subscription. */
  endpoint: string;
}

/** A row of `GET /push/registrations`, for the list in Settings. */
export interface RegistrationRow {
  id: number;
  transport: string;
  label: string;
  createdAt: number;
  lastOkAt: number | null;
}

/**
 * The daemon does not serve this feature.
 *
 * A distinct type rather than a status code, because every caller wants the
 * same thing when it happens -- stop, and render nothing -- and none of them
 * wants to know which of the five routes was the one that 404ed.
 */
class PushUnavailable extends Error {
  constructor() {
    super("this daemon does not serve push notifications");
    this.name = "PushUnavailable";
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  // Through the transport, not `fetch`. In a browser they are the same call.
  // In the desktop shell they are not: the webview's origin is the Tauri asset
  // protocol, so a raw relative fetch never reaches the daemon at all -- on
  // Linux the asset resolver answered `/push/vapid` with `index.html` and a
  // 200, which read as "push is supported" until the JSON parse failed, and on
  // macOS the same fetch throws outright, which left this module `unknown`
  // forever and the settings page blank behind a spinner. The transport is the
  // one place that knows where the daemon is and how a packaged build
  // authenticates; every other daemon route here already goes through it.
  const res = await transportRequest(path, {
    method,
    credentials: "same-origin",
    // On every request including the bodyless ones: the daemon refuses a
    // cookie-authenticated write without it, and that refusal is its CSRF
    // defence rather than an inconvenience to route around.
    // `authHeaders` rather than a bare content-type: it sends the same
    // content-type and adds the bearer when this build has one. A browser has
    // none and is authenticated by its cookie; a packaged build has no cookie,
    // and this line is the whole of what makes the request authenticated at
    // all. See `lib/settings.ts` for what leaving it out cost.
    headers: authHeaders(),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  // 404 is the honest answer from a daemon built before this feature, and 501
  // is the honest answer from one that routes `/push` and has no notifier
  // configured. Both mean the same thing to every caller.
  if (res.status === 404 || res.status === 501) {
    supported.value = "no";
    throw new PushUnavailable();
  }
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const problemBody = await res.json();
      detail = problemBody.error ?? problemBody.detail ?? detail;
    } catch {
      // A proxy error page is HTML; the status line is the message.
    }
    throw new Error(detail);
  }
  if (res.status === 204) {
    supported.value = "yes";
    return undefined as T;
  }
  // Parsed before `supported` is written, in that order deliberately: a 200
  // that is not JSON is a proxy's error page or an SPA fallback wearing a
  // success code, and it is evidence of a broken path rather than of a daemon
  // that serves push. Claiming "yes" first is exactly how the Linux desktop
  // ended up offering Web Push enrolment against its own index.html.
  const parsed = (await res.json()) as T;
  supported.value = "yes";
  return parsed;
}

/** §11: `GET /push/vapid -> 200 { "publicKey": "<base64url P-256>" }`. */
async function vapidKey(): Promise<string> {
  const body = await request<{ publicKey?: string }>("GET", "/push/vapid");
  const key = body.publicKey;
  if (typeof key !== "string" || key.length === 0) {
    throw new Error("the daemon returned no VAPID public key");
  }
  return key;
}

/** §11: `GET /push/registrations`. Every device, not just this one. */
export function listRegistrations(): Promise<RegistrationRow[]> {
  return request<RegistrationRow[]>("GET", "/push/registrations");
}

/**
 * §11: `DELETE /push/registrations/{id}`. For a device that is not this one.
 *
 * Silencing the phone you have lost is the same argument the session list
 * makes, and it is the only half available from here: the subscription itself
 * lives in that other browser's profile and no origin can reach it. The row is
 * the part that matters -- with it gone nothing is delivered, and the other
 * device's next launch re-registers if it is still yours.
 *
 * {@link disable} is what this device uses instead, because it has the other
 * half to clean up as well.
 */
export function removeRegistration(id: number): Promise<void> {
  return request<void>("DELETE", `/push/registrations/${id}`);
}

/**
 * §11: `POST /push/test -> 202`.
 *
 * Worth a button of its own. Without one, the first time anybody discovers that
 * the VAPID key is wrong or that the box cannot make outbound HTTPS is when
 * mail arrives and nothing happens -- a non-event, with nothing to search for.
 */
export function sendTest(): Promise<void> {
  return request<void>("POST", "/push/test");
}

/**
 * This tenant's notification preferences, as `/push/prefs` carries them.
 *
 * The defaults restate the daemon's, for the case where the request fails and
 * a screen still has to render something. They must not drift: `detail` narrow
 * because being wrong there leaks to whoever can see the screen, `notifyBulk`
 * on because being wrong there is silent -- nobody goes looking for a
 * notification they never got.
 */
export type NotifyPrefs = {
  /**
   * `none`, `sender`, `sender_and_subject`, or `sender_subject_and_preview`.
   * The daemon narrows an unrecognised value rather than echoing it.
   */
  detail: string;
  notifyBulk: boolean;
  enabled: boolean;
};

/** §11: `GET /push/prefs`. What this tenant has chosen. */
export function getPrefs(): Promise<NotifyPrefs> {
  return request<NotifyPrefs>("GET", "/push/prefs");
}

/**
 * §11: `PUT /push/prefs`. The whole object, because the daemon replaces rather
 * than merges -- three fields and no merge rules to get wrong.
 *
 * Answers with what was *stored*, which is not always what was sent: an
 * unrecognised `detail` is narrowed on the way in, so a caller that assumed
 * its own request won would show a setting the daemon does not hold. Render
 * the answer, not the argument.
 */
export function setPrefs(wanted: NotifyPrefs): Promise<NotifyPrefs> {
  return request<NotifyPrefs>("PUT", "/push/prefs", wanted);
}

/**
 * Ask whether this daemon has push, without doing anything else.
 *
 * Settings calls this on mount so it knows whether to render the card. It is
 * one request per page load; the result is cached in {@link supported}, and a
 * daemon does not grow the feature between two renders.
 */
export async function probe(): Promise<void> {
  if (supported.value !== "unknown") return;
  try {
    await vapidKey();
  } catch (e) {
    // A 404 has already set the signal. Anything else -- a 500, a dropped
    // connection -- is not evidence that the feature is missing, so leave it
    // unknown and let the next mount ask again.
    if (!(e instanceof PushUnavailable)) supported.value = "unknown";
  }
}

// ---- the subscription -------------------------------------------------------

/**
 * One at a time.
 *
 * Two clicks on "Turn on" would otherwise run two `subscribe()` calls and two
 * registrations. The browser would hand back the same endpoint and the UPSERT
 * would collapse them, so the damage is bounded -- but relying on the far end
 * to clean up after a race this file can simply not have is the wrong way round.
 */
let inFlight: Promise<void> | null = null;

/**
 * Turn notifications on for this device, from a click.
 *
 * The order is load-bearing. Permission first, because everything after it is
 * pointless without one and because the browser will only show the prompt while
 * it can still see the gesture that led here. Then the key, then the
 * subscription, then the row.
 */
export function enable(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = enableOnce().finally(() => {
    inFlight = null;
    busy.value = false;
  });
  return inFlight;
}

async function enableOnce(): Promise<void> {
  busy.value = true;
  problem.value = null;
  if (!browserCapable) {
    problem.value = "This browser cannot receive push notifications.";
    return;
  }
  // A push subscription belongs to the *browser*, not the session: it would
  // keep this account's mail landing on a shared machine after the person
  // left. The daemon reaps the registration with the twelve-hour session, but
  // half a day of someone else's screen showing your senders is not what the
  // public-computer box promised. Refused with a sentence rather than hidden,
  // because a control that vanishes looks broken.
  if (isPublicSession()) {
    problem.value =
      "Not on a public computer — this browser would keep showing this account's mail after you leave.";
    return;
  }

  try {
    const granted = await Notification.requestPermission();
    permission.value = granted;
    if (granted !== "granted") {
      // Not an error to report. Someone said no, or dismissed the prompt; the
      // UI already says what each answer means.
      return;
    }

    const registration = await navigator.serviceWorker.ready;
    const key = await vapidKey();
    const subscription = await subscribeWith(registration, key);
    enabled.value = await enrol(subscription);
  } catch (e) {
    if (e instanceof PushUnavailable) return;
    problem.value = describe(e);
  }
}

/**
 * The existing subscription if there is one, a new one if there is not.
 *
 * The key comparison is the part that is easy to leave out and expensive to
 * omit. A browser pins `applicationServerKey` at subscribe time, so after the
 * operator rotates the tenant's VAPID key the old subscription still exists,
 * still looks healthy from here, and can never be delivered to again -- and
 * `subscribe()` with a different key throws `InvalidStateError` rather than
 * replacing it. Unsubscribing first is the only way through, and doing it only
 * when the keys actually differ is what keeps the ordinary launch free.
 */
async function subscribeWith(
  registration: ServiceWorkerRegistration,
  key: string,
): Promise<PushSubscription> {
  const existing = await registration.pushManager.getSubscription();
  if (existing) {
    if (base64url(existing.options.applicationServerKey) === key) return existing;
    await existing.unsubscribe();
  }
  return registration.pushManager.subscribe({
    // Mandatory in Chrome, and honest: every push this client receives results
    // in a notification. See the `push` handler in `public/sw.js` for why that
    // promise is kept even when the tab is already open.
    userVisibleOnly: true,
    applicationServerKey: decodeKey(key),
  });
}

/** §11: `POST /push/registrations -> 201 { "id": 7 }`. Idempotent by design. */
async function enrol(subscription: PushSubscription): Promise<Enrolment> {
  const keys = subscription.toJSON().keys ?? {};
  const created = await request<{ id: number }>("POST", "/push/registrations", {
    transport: "webpush",
    endpoint: subscription.endpoint,
    keys: { p256dh: keys["p256dh"], auth: keys["auth"] },
    label: describeDevice(),
  });
  const enrolment: Enrolment = { id: created.id, endpoint: subscription.endpoint };
  write(enrolment);
  return enrolment;
}

/**
 * Turn them off, on both sides.
 *
 * Doing one without the other leaves a phantom or a corpse: a server row with
 * no subscriber still costs a delivery per sync pass until it 410s, and a live
 * subscription with no row is a permission the user believes they revoked.
 *
 * Server first. If the local `unsubscribe()` then fails, nothing will ever push
 * to it again, which is the harmless direction; the other order leaves a row
 * the user asked to remove and no id left to remove it with.
 */
export async function disable(): Promise<void> {
  busy.value = true;
  problem.value = null;
  try {
    const enrolment = enabled.peek();
    if (enrolment) {
      try {
        await request<void>("DELETE", `/push/registrations/${enrolment.id}`);
      } catch (e) {
        // Already gone is the outcome we wanted. The daemon reaps rows on a
        // 410 from the push service, so a row this client has lost track of is
        // not a row that will be lost.
        if (!(e instanceof PushUnavailable)) throw e;
      }
    }
    if (browserCapable) {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) await subscription.unsubscribe();
    }
    enabled.value = null;
    write(null);
  } catch (e) {
    problem.value = describe(e);
  } finally {
    busy.value = false;
  }
}

/**
 * Re-register on launch, and only then.
 *
 * §11 asks clients to re-register every launch, and the reasons are real: the
 * UPSERT re-points the row at the session that is signed in *now*, so a device
 * that logged out and back in is not silenced by the revoked token's cascade,
 * and it zeroes `failures` so a device that was unreachable for an afternoon is
 * not reaped by the ten-failure ceiling.
 *
 * What it must not do is create anything. It never calls `subscribe()` and
 * never asks for permission: someone who turned this off, or who never turned
 * it on, gets no request at all -- which is also why a daemon without `/push`
 * costs an ordinary user nothing on load.
 */
export async function syncOnLaunch(): Promise<void> {
  if (!browserCapable || Notification.permission !== "granted") return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;
    const key = await vapidKey();
    if (base64url(subscription.options.applicationServerKey) !== key) {
      // The tenant's key was rotated while this device was away. Nothing can
      // be delivered to this subscription any more, and there is no permission
      // prompt in the way, so replacing it now is silent and correct.
      await subscription.unsubscribe();
      const replacement = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: decodeKey(key),
      });
      enabled.value = await enrol(replacement);
      return;
    }
    enabled.value = await enrol(subscription);
  } catch {
    // Nothing here was asked for by a person, so nothing here reports. The
    // Settings screen will say what is true when someone next looks at it.
  }
}

// ---- the worker -------------------------------------------------------------

/**
 * Register the worker and listen to it. Called once, from `main.tsx`.
 *
 * Registered on every load rather than only when push is on, because the worker
 * is also what answers a notification click for a device that was enrolled from
 * another tab, and because `navigator.serviceWorker.ready` has to resolve
 * before anything else in this file can run.
 *
 * The update check is the insurance for the case this whole feature creates: an
 * installed web app that is opened, backgrounded and foregrounded for weeks
 * without ever performing a navigation, and therefore without the browser ever
 * re-fetching `sw.js`. One conditional GET when it comes back to the foreground,
 * at most once an hour, is a cheap way not to be the app that shipped a
 * permanent bug.
 */
export function watchServiceWorker(): void {
  if (!("serviceWorker" in navigator)) return;

  navigator.serviceWorker.addEventListener("message", (event) => onWorkerMessage(event.data));

  void navigator.serviceWorker
    .register("/sw.js", { scope: "/" })
    .then((registration) => {
      let checked = Date.now();
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState !== "visible") return;
        if (Date.now() - checked < 3_600_000) return;
        checked = Date.now();
        void registration.update().catch(() => {
          /* offline, or the daemon is down. Next time. */
        });
      });
    })
    .catch(() => {
      // A registration failure is not a reason to fail the app. It happens on
      // an insecure origin, in private windows in some browsers, and wherever
      // an enterprise policy says so; none of those stop mail from working.
    });
}

function onWorkerMessage(data: unknown): void {
  if (typeof data !== "object" || data === null) return;
  const message = data as { type?: string; url?: string; id?: number; endpoint?: string };
  switch (message.type) {
    case "hm:push":
      // A push arrived while a window was open, so the worker suppressed the
      // notification and told the app instead -- §2's layering, where a running
      // client is served by the live stream rather than by the system tray.
      // Asking here is not redundant with `lib/events.ts`: the stream can be
      // mid-reconnect, and this is a change the server has just proved happened.
      // The same delta the stream would have run, for the same reason -- a push
      // about mail filed somewhere nobody is looking at should cost as little
      // as the event about it would.
      void delta.sync();
      break;
    case "hm:open":
      if (typeof message.url === "string") openFrom(message.url);
      break;
    case "hm:push-registration":
      // The worker handled `pushsubscriptionchange` and the row has a new id.
      if (typeof message.id === "number" && typeof message.endpoint === "string") {
        const enrolment = { id: message.id, endpoint: message.endpoint };
        enabled.value = enrolment;
        write(enrolment);
      }
      break;
    default:
      break;
  }
}

// ---- the deep link ----------------------------------------------------------

/**
 * The one URL shape this client understands, and therefore the one the daemon
 * should put in a notification's `navigate`:
 *
 *     /?open=<accountId>:<emailId>
 *
 * Those are the JMAP account id and `Email` id, in the same order and with the
 * same separator `store.keyOf` already uses for the row key. There is no
 * per-message URL otherwise -- `lib/navigation.ts` explains why the app has no
 * router -- so this exists for exactly one caller and is stripped the moment it
 * has been read.
 *
 * Stripping matters for the same reason it does after a Google authorisation: a
 * reload would otherwise re-open a message the user closed, forever, and the URL
 * they copy out of the address bar would carry it to someone else.
 */
export function takeOpenTarget(): void {
  const params = new URLSearchParams(window.location.search);
  const target = params.get("open");
  if (!target) return;
  params.delete("open");
  const query = params.toString();
  window.history.replaceState(
    null,
    "",
    `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`,
  );
  pending.value = target;
}

/**
 * A message a notification click asked for, waiting for somewhere to put it.
 *
 * The click may have *launched* the app: at that point nothing has asked the
 * daemon who we are, there is no session, and a `getEmail` would 401 and put
 * the login screen on screen with an error toast about a message. So the target
 * is parked here and `app.tsx` spends it once the app is signed in -- which is
 * also the right behaviour when the credential really has expired, because the
 * message opens after the login instead of being lost to it.
 */
export const pending = signal<string | null>(null);

/** Called by `app.tsx` when there is a session to open a message into. */
export function takePending(): void {
  const target = pending.peek();
  if (!target) return;
  pending.value = null;
  void open(target);
}

function openFrom(url: string): void {
  const target = new URL(url, window.location.origin).searchParams.get("open");
  if (!target) return;
  // Through the same queue as a cold start: a click that focuses a tab whose
  // session expired an hour ago is in exactly the position a launch is.
  pending.value = target;
  if (store.session.peek()) takePending();
}

async function open(target: string): Promise<void> {
  const at = target.indexOf(":");
  if (at <= 0) return;
  const accountId = target.slice(0, at);
  const emailId = target.slice(at + 1);
  if (!emailId) return;
  try {
    // Fetched here and then handed to `store.open`, which fetches it again.
    // That is one extra round trip on a notification tap, in exchange for the
    // reader having exactly one way of being opened -- the alternative is a
    // second copy of the cursor, loading and error handling in `store`, which
    // is how two ways of opening a message start to disagree.
    const envelope = await jmap.getEmail(accountId, emailId);
    store.view.value = "mail";
    await store.open(envelope);
  } catch (e) {
    // The message was deleted from another device between the push and the
    // tap. Say so rather than opening an empty reader.
    store.toast(describe(e), "error");
  }
}

// ---- notification action buttons -------------------------------------------

/**
 * Act on a button pressed on a desktop notification. Called once, from
 * `app.tsx`, and a no-op in a browser, where the transport never emits one.
 *
 * The address arrives as the notification's own `navigate` --
 * `hydramail://message/<account>/<id>`, the deep-link spelling with the
 * server's ids, because `core/notify.rs` replaces the policy-rendered address
 * with exactly that (its module header is about nothing else). So it is parsed
 * by {@link messageFromLink}, the deep-link parser, not by this file: the first
 * version read it as the web click's `/?open=` shape instead, `get("open")`
 * answered null on every press, and the buttons did a silent nothing.
 * What differs from a click is everything after the parse: the message is not
 * opened and the window is not focused, because the button's promise is that
 * the mail is dealt with *without* going to it.
 *
 * The operations go through `lib/actions` rather than a private `Email/set`,
 * so a row that is on screen updates optimistically and rolls back on refusal
 * exactly as if the same button had been pressed inside the app.
 */
export function watchToastActions(): void {
  onToastAction(({ action, navigate }) => void handleToastAction(action, navigate));
}

/** The work behind {@link watchToastActions}, separated so a test can press. */
export async function handleToastAction(action: string, navigate: string): Promise<void> {
  const target = messageFromLink(navigate);
  if (!target) return;
  try {
    const envelope = await jmap.getEmail(target.account, target.email);
    if (action === "archive") await actions.moveToRole([envelope], "archive");
    else if (action === "mark-read") await actions.markSeen([envelope], true);
  } catch (e) {
    // The message went away between the notification and the button, or
    // the session expired. Say so; silently doing nothing would leave a
    // notification that lies about what pressing it did.
    store.toast(describe(e), "error");
  }
}

// ---- odds and ends ----------------------------------------------------------

/**
 * A label for the registration list, matching the one a login sends.
 *
 * Coarse for the same reason `describeBrowser` in `lib/auth.ts` is: it is
 * stored on the server and rendered in a list of devices, and a full user-agent
 * string would be a fingerprint kept somewhere it is not needed to answer "is
 * this the phone or the laptop".
 */
function describeDevice(): string {
  const ua = navigator.userAgent;
  const engine = /Firefox\//.test(ua)
    ? "Firefox"
    : /Edg\//.test(ua)
      ? "Edge"
      : /Chrome\//.test(ua)
        ? "Chrome"
        : /Safari\//.test(ua)
          ? "Safari"
          : "Browser";
  const platform = /Android/.test(ua)
    ? "Android"
    : /iPhone|iPad/.test(ua)
      ? "iOS"
      : /Mac OS X/.test(ua)
        ? "macOS"
        : /Windows/.test(ua)
          ? "Windows"
          : /Linux/.test(ua)
            ? "Linux"
            : "";
  return platform ? `${engine} on ${platform}` : engine;
}

/**
 * base64url, without padding, which is what RFC 8292 and every push service
 * speak and what `atob`/`btoa` do not.
 */
function base64url(buffer: ArrayBuffer | null): string {
  if (!buffer) return "";
  let binary = "";
  for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/gu, "-").replace(/\//gu, "_").replace(/=+$/u, "");
}

/**
 * The server's key, as bytes.
 *
 * `applicationServerKey` accepts a base64url string in current Chrome and
 * Firefox, and the bytes everywhere -- including in the Safari versions that
 * matter here, since iOS 16.4 is the floor for this feature. Decoding is four
 * lines and removes the question.
 */
function decodeKey(key: string): ArrayBuffer {
  const padded = key.replace(/-/gu, "+").replace(/_/gu, "/");
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, "="));
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function describe(e: unknown): string {
  if (e instanceof Error) {
    // The three the Push API throws that a person can act on. Chrome's own
    // message for the first is "Registration failed - push service error",
    // which tells nobody anything.
    if (e.name === "NotAllowedError") return "The browser refused: notifications are blocked here.";
    if (e.name === "NotSupportedError") return "This browser cannot subscribe to push notifications.";
    if (e.name === "AbortError") {
      return "The browser's push service could not be reached. Check the network and try again.";
    }
    return e.message;
  }
  return String(e);
}

const STORAGE_KEY = "hm.push";

/**
 * Which row on the server is this device.
 *
 * Kept locally because `GET /push/registrations` deliberately does not return
 * endpoints -- an endpoint is a capability to send, and a list endpoint that
 * handed them back would be a way to harvest them -- so there is nothing in
 * that response this client could match itself against. The id from the 201 is.
 */
function read(): Enrolment | null {
  // A public session does not claim a row it did not create. The id here says
  // "this browser is that registration on the server", which on a shared
  // machine would be a claim about the previous person's device -- and the one
  // thing this id is used for is deleting that row.
  if (isPublicSession()) return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Enrolment;
    return typeof value?.id === "number" && typeof value?.endpoint === "string" ? value : null;
  } catch {
    return null;
  }
}

function write(value: Enrolment | null): void {
  try {
    // Never stored in a public session, and actively removed instead: whatever
    // is under this key belongs to the machine rather than to the person, and
    // leaving it would leave one behind for the next of them.
    if (value && !isPublicSession()) localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private browsing throws on access. Losing the id costs the ability to
    // delete this row by id; the daemon still reaps it on the first 410.
  }
}
